import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../utils/api";
import { Notification, NotificationResponse } from "../../types/cs";

interface UseNotificationParams {
  pageSize?: number;
}

interface UseNotificationReturn {
  notifications: Notification[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

const useNotification = (params: UseNotificationParams = {}): UseNotificationReturn => {
  const { pageSize = 20 } = params;
  
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchNotifications = useCallback(async (page: number = 1, isAppend: boolean = false) => {
    if (page === 1) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    setError("");

    try {
      const response = await apiClient.get("/api/v1/cs/notification", { 
        params: { p: page }
      });
      
      const responseData: NotificationResponse = response.data;

      if (responseData.status) {
        const newNotifications = responseData.data;
        
        if (isAppend) {
          setNotifications(prev => [...prev, ...newNotifications]);
        } else {
          setNotifications(newNotifications);
        }

        setHasMore(newNotifications.length >= pageSize);
        setCurrentPage(page);
      } else {
        setError("공지사항을 불러오는데 실패했습니다.");
      }
    } catch (err) {
      console.error("공지사항 조회 오류:", err);
      setError("네트워크 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [pageSize]);

  const loadMore = useCallback(async () => {
    if (hasMore && !isLoadingMore && !isLoading) {
      await fetchNotifications(currentPage + 1, true);
    }
  }, [hasMore, isLoadingMore, isLoading, currentPage, fetchNotifications]);

  const refresh = useCallback(async () => {
    setCurrentPage(1);
    setHasMore(true);
    await fetchNotifications(1, false);
  }, [fetchNotifications]);

  useEffect(() => {
    fetchNotifications(1, false);
  }, [fetchNotifications]);

  return {
    notifications,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
    refresh,
  };
};

export default useNotification;
export type { UseNotificationParams, UseNotificationReturn }; 